import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Link } from 'react-router-dom';
import PillSpinner from '../components/PillSpinner';

const API_URL = 'http://localhost:5000/api';

const Reports = () => {
    const [dailySales, setDailySales] = useState([]);
    const [topMedicines, setTopMedicines] = useState([]);
    const [expiringMedicines, setExpiringMedicines] = useState([]);
    const [expiredMedicines, setExpiredMedicines] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchReports = async () => {
            try {
                const [dailyRes, topRes, expiringRes, expiredRes] = await Promise.all([
                    axios.get(`${API_URL}/sales/daily`),
                    axios.get(`${API_URL}/sales/top-medicines`),
                    axios.get(`${API_URL}/medicines/expiring`),
                    axios.get(`${API_URL}/medicines/expired`)
                ]);
                setDailySales(dailyRes.data);
                setTopMedicines(topRes.data);
                setExpiringMedicines(expiringRes.data);
                setExpiredMedicines(expiredRes.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchReports();
    }, []);

    if (loading) return <PillSpinner />;

    return (
        <div>
            {/* Daily Sales Chart */}
            <div className="card">
                <h2>📈 Daily Sales Trend (Last 7 Days)</h2>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dailySales}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                        <Legend />
                        <Bar dataKey="amount" fill="#1a8f4e" name="Sales (₹)" />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {/* Top Selling Medicines */}
            <div className="card">
                <h2>🏆 Top Selling Medicines</h2>
                {topMedicines.length === 0 ? (
                    <p>No sales data yet.</p>
                ) : (
                    <table className="inventory-table">
                        <thead>
                            <tr>
                                <th>Medicine Name</th>
                                <th>Quantity Sold</th>
                                <th>Revenue (₹)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {topMedicines.map((med, idx) => (
                                <tr key={idx}>
                                    <td>{med.name}</td>
                                    <td>{med.quantity}</td>
                                    <td>₹{med.amount.toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Medicines Expiring Within 30 Days */}
            <div className="card">
                <h2>⚠️ Medicines Expiring Within 30 Days</h2>
                {expiringMedicines.length === 0 ? (
                    <p className="good">✅ No medicines expiring soon.</p>
                ) : (
                    <table className="inventory-table">
                        <thead>
                            <tr>
                                <th>Medicine Name</th>
                                <th>Stock</th>
                                <th>Expiry Date</th>
                                <th>Days Left</th>
                            </tr>
                        </thead>
                        <tbody>
                            {expiringMedicines.map(med => {
                                const daysLeft = Math.ceil((new Date(med.expiryDate) - new Date()) / (1000 * 3600 * 24));
                                return (
                                    <tr key={med._id}>
                                        <td>{med.name}</td>
                                        <td>{med.stock}</td>
                                        <td>{new Date(med.expiryDate).toLocaleDateString()}</td>
                                        <td style={{ color: daysLeft <= 7 ? 'red' : 'orange' }}>{daysLeft} days</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Expired Medicines - NEW SECTION */}
            <div className="card" style={{ border: '2px solid #dc3545' }}>
                <h2 style={{ color: '#dc3545' }}>❌ Expired Medicines (Past Expiry)</h2>
                {expiredMedicines.length === 0 ? (
                    <p className="good">✅ No expired medicines.</p>
                ) : (
                    <table className="inventory-table">
                        <thead>
                            <tr>
                                <th>Medicine Name</th>
                                <th>Stock</th>
                                <th>Expiry Date</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {expiredMedicines.map(med => (
                                <tr key={med._id} style={{ backgroundColor: '#ffebee' }}>
                                    <td>{med.name}</td>
                                    <td>{med.stock}</td>
                                    <td>{new Date(med.expiryDate).toLocaleDateString()}</td>
                                    <td style={{ color: 'red', fontWeight: 'bold' }}>EXPIRED</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default Reports;